import { Divider, HStack, useColorMode, useDisclosure, VStack } from '@chakra-ui/react'
import { selectAuthenticatedUser, useSelector } from '@missionlabs/api'
import {
    Button,
    FormProvider,
    Input,
    Tooltip,
    useToast,
    useYupForm,
    ValidatedInput,
    VirtualPaginationContextProvider,
    yup
} from '@missionlabs/react'
import { HelpIcon } from '@missionlabs/react/circleloop'
import { NewSpeedDial, SpeedDial, UpdateSpeedDial, UserRole } from '@missionlabs/types'
import ContactsProvider from 'features/contacts/context/contactsContext'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useUserRole } from 'shared/hooks/useUserRole'
import {
    useCreateSpeedDialForClientMutation,
    useCreateSpeedDialForUserMutation,
    useUpdateSpeedDialForClientMutation,
    useUpdateSpeedDialForUserMutation
} from 'shared/store'

import { ContactList } from './ContactList'
import { SelectedContact } from './ContactRow'

export interface SpeedDialFormProps {
    onClose: () => void
    values: SpeedDial
    mode: 'edit' | 'add'
}

export const defaultValues: NewSpeedDial = {
    speedDialCode: '',
    name: '',
    replacementNumber: ''
}

export const SpeedDialForm: FC<SpeedDialFormProps> = ({ onClose, values, mode }) => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const userRole = useUserRole()
    const { pathname } = useLocation()

    const { userID = '' } = useSelector(selectAuthenticatedUser) ?? {}

    const { colorMode } = useColorMode()

    const [selectedContact, setSelectedContact] = useState<SelectedContact>()

    const isAdmin = pathname.includes('/admin/') && userRole === UserRole.ADMIN

    const { isOpen: isChoosingContact, onOpen: setChoosingContact } = useDisclosure({
        defaultIsOpen: mode === 'add'
    })

    const createRequest = isAdmin
        ? useCreateSpeedDialForClientMutation
        : useCreateSpeedDialForUserMutation

    const updateRequest = isAdmin
        ? useUpdateSpeedDialForClientMutation
        : useUpdateSpeedDialForUserMutation

    const [createSpeedDial, { isLoading: isCreateLoading }] = createRequest()
    const [updateSpeedDial, { isLoading: isUpdateLoading }] = updateRequest()

    const speedDialSchema = yup.object({
        speedDialCode: yup
            .string()
            .required()
            .matches(/^\d+$/, 'Must be only digits')
            .min(2)
            .max(2),
        name: yup.string().required()
    })

    const methods = useYupForm<NewSpeedDial>(speedDialSchema, {
        values
    })

    const {
        formState: { isSubmitting, errors },
        handleSubmit
    } = methods

    const handleCreate = useCallback(
        async (data: NewSpeedDial | UpdateSpeedDial) => {
            if (!data || !userID) return

            if (!isAdmin) {
                data['userID'] = userID
            }

            data['replacementNumber'] = isChoosingContact
                ? selectedContact!.numberE164
                : data.replacementNumber

            try {
                // @ts-ignore
                await createSpeedDial(data).unwrap()
                toast({ status: 'success', title: `Created speed dial` })
                onClose()
            } catch (e) {
                console.error(e)
                const error = e.data?.message || e.message
                toast({ status: 'error', title: error || 'Failed to create speed dial' })
            }
        },
        [createSpeedDial, isAdmin, isChoosingContact, onClose, selectedContact, toast, userID]
    )

    const onSubmitUpdate = useCallback(
        async (data: UpdateSpeedDial | NewSpeedDial) => {
            if (!data || !userID) return

            if (!values) {
                console.warn('No editing data')
                return
            }

            if (isChoosingContact && !selectedContact?.numberE164) {
                toast({ status: 'error', title: `Please select a number from the contact list` })
                return
            }

            const payload = {
                replacementNumber: isChoosingContact
                    ? selectedContact!.numberE164
                    : data.replacementNumber,
                speedDialCode: data.speedDialCode,
                oldSpeedDialCode: values.speedDialCode,
                name: data.name
            }

            if (!isAdmin) {
                payload['userID'] = userID
            }

            try {
                // @ts-ignore
                await updateSpeedDial(payload).unwrap()
                toast({ status: 'success', title: 'Updated speed dial ' })
                onClose()
            } catch (e) {
                console.error(e)
                const error = e.data?.message || e.message
                toast({ status: 'error', title: error || 'Failed to update speed dial' })
            }
        },
        [
            values,
            isAdmin,
            isChoosingContact,
            onClose,
            selectedContact,
            toast,
            updateSpeedDial,
            userID
        ]
    )

    const onSubmit = mode === 'edit' ? onSubmitUpdate : handleCreate

    const speedDialCodeLabel = useMemo(() => {
        if (isAdmin) return t('settings.speedDial.code')

        return (
            <div>
                {t('settings.speedDial.code')}
                <Tooltip
                    label={t('settings.speedDial.overrideHelperText')}
                    shouldWrapChildren={true}
                    placement="bottom"
                >
                    <HelpIcon
                        boxSize="24px"
                        p="4px"
                        color={`${colorMode}.alerts.selectiveYellow`}
                    />
                </Tooltip>
            </div>
        )
    }, [colorMode, t, isAdmin])

    return (
        <FormProvider {...methods}>
            <VStack
                onSubmit={handleSubmit(onSubmit)}
                as="form"
                h="full"
                spacing="24px"
                p="24px"
                align="stretch"
                justify="space-between"
            >
                <VStack align="stretch" gap="24px">
                    <ValidatedInput
                        containerSx={{
                            mb:
                                (errors.speedDialCode?.message?.length || 0) > 40
                                    ? '24px'
                                    : undefined
                        }}
                        name="speedDialCode"
                        label={speedDialCodeLabel as any}
                        errorSx={{ whiteSpace: 'pre-wrap' }}
                    >
                        <Input w="100px" placeholder={'#'} />
                    </ValidatedInput>

                    <ValidatedInput name="name" label={t('settings.speedDial.name')}>
                        <Input />
                    </ValidatedInput>

                    <Divider />

                    <VStack spacing="10px" align="inherit">
                        {!isChoosingContact ? (
                            <HStack>
                                <ValidatedInput label={t('Number')} name="replacementNumber">
                                    <Input isDisabled />
                                </ValidatedInput>
                                <Button onClick={setChoosingContact} alignSelf="end">
                                    {t('Add new')}
                                </Button>
                            </HStack>
                        ) : (
                            <VirtualPaginationContextProvider>
                                <ContactsProvider>
                                    <ContactList
                                        onSelect={setSelectedContact}
                                        selectedContact={selectedContact}
                                    />
                                </ContactsProvider>
                            </VirtualPaginationContextProvider>
                        )}
                    </VStack>
                </VStack>
                <HStack spacing="16px" align="inherit">
                    <Button
                        w="full"
                        size="lg"
                        variant="secondary"
                        isDisabled={isSubmitting}
                        onClick={onClose}
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        type="submit"
                        w="full"
                        size="lg"
                        variant="creationary"
                        isLoading={isCreateLoading || isUpdateLoading}
                    >
                        {mode === 'edit' ? t('Update') : t('Add')}
                    </Button>
                </HStack>
            </VStack>
        </FormProvider>
    )
}
